import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProjectCaseStudy.module.css';

const IMG_BASE = "/assets/project/Mechanical Properties of 3D Printed Recycled Plastics (rPET)";

const Figure = ({ src, alt, caption }) => (
  <figure className={styles.figureCard}>
    <img src={src} alt={alt} className={styles.galleryImage} />
    <figcaption className={styles.galleryCaption}>
      {caption}
    </figcaption>
  </figure>
);

export default function RPETProjectPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.articleContainer}>

      <button className={styles.backButton} onClick={() => navigate('/projects')}>
        ← Back to Projects
      </button>

      <header className={styles.header}>
        <h1 className={styles.title}>
          Sustainable Additive Manufacturing Using Recycled PET (rPET)
        </h1>
        <div className={styles.authors}>
          <span className={styles.highlightAuthor}>Jolok Banarjee (Project Leader)</span> &nbsp;•&nbsp; Jul 2025 – Mar 2026
        </div>
        <div className={styles.affiliations}>
          Aviation and Aerospace University, Bangladesh (AAUB) — Supervised by Shahrukh Khan (Assistant Professor, Dept. of Aerospace Engineering)
        </div>
        <div className={styles.badge} style={{ background: 'rgba(217, 119, 6, 0.1)', color: '#d97706', border: '1px solid rgba(217, 119, 6, 0.3)' }}>
          🥈 AIAA 2025 Region VII Student Conference — Second Place Tie (Team Category)
        </div>
        <div className={styles.headerActions}>
          <a
            href="https://aiaa.org/2025/12/09/aiaa-announces-2025-region-vii-student-conference-winners/"
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: '#d97706',
              textDecoration: 'none',
              background: 'rgba(217, 119, 6, 0.06)',
              padding: '0.5rem 1.05rem',
              borderRadius: '8px',
              border: '1px solid rgba(217, 119, 6, 0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
          >
            🏆 AIAA Press Release →
          </a>
          <button
            onClick={() => navigate('/research/rpet-additive-manufacturing')}
            className={styles.btnPrimary}
            style={{ padding: '0.5rem 1.05rem', fontSize: '0.85rem' }}
          >
            📄 View Research Page →
          </button>
        </div>
      </header>

      <div className={styles.abstractBox}>
        <h3 className={styles.abstractTitle}>Project Overview</h3>
        <p className={styles.abstractText}>
          As global plastic waste continues to grow exponentially, there is an urgent engineering mandate for practical, decentralized recycling systems. In this research, I led the development of an Arduino-controlled extrusion system for producing 1.75 mm 3D printing filament from post-consumer recycled polyethylene terephthalate (rPET). The project spanned the entire materials pipeline: hardware design, conducting tensile testing following ASTM D638 on 45 specimens across nine parameter combinations benchmarked against commercial polymers (PLA, ABS, PETG), and developing an XGBoost machine learning model to optimize mechanical performance and predict non-linear stress-strain behaviors. The full manuscript is currently under review in <em>Rapid Prototyping Journal</em>, and an earlier version received Second Place (Tie) in the Team Category at the 2025 AIAA Region VII Student Conference.
        </p>
        <div className={styles.keywords}>
          <strong>Focus Areas:</strong> Additive Manufacturing, Materials Science, Filament Extrusion, Machine Learning (XGBoost), Circular Economy.
        </div>
      </div>

      <section>
        <h2 className={styles.sectionTitle}>Filament Extrusion & Material Recovery</h2>
        <p className={styles.paragraph}>
          The first phase required designing a reliable hardware extrusion system capable of processing post-consumer PET bottle strips into consistent Fused Filament Fabrication (FFF) grade spools. The custom-built machine integrates a mechanical strip cutter, a PID-controlled heating block (REX-C100 thermostat + SSR-25DD + 40W MK8 heater), and an Arduino-driven NEMA 17 winding mechanism.
        </p>
        <p className={styles.paragraph}>
          Extrusion temperatures, motor torque, and cooling rates were precisely calibrated to prevent material crystallization and brittleness. The optimized system achieved an average material recovery efficiency of <strong>53.8%</strong>, successfully proving the computational and environmental viability of converting local post-consumer waste into high-performance engineering filament.
        </p>
        <div className={styles.galleryGrid}>
          <Figure
            src={`${IMG_BASE}/cover.jpg`}
            alt="rPET Extrusion Machine"
            caption="Figure 1: The developed extrusion system showing the strip holder, heating block, filament winder, and closed-loop temperature control system."
          />
          <Figure
            src={`${IMG_BASE}/buttol.jpg`}
            alt="rPET Strips"
            caption="Figure 2: Post-consumer PET bottles converted into continuous strips (8.5–9.0 mm width) ready for thermal extrusion."
          />
          <Figure
            src={`${IMG_BASE}/rPET_filament.jpg`}
            alt="rPET Filament Spools"
            caption="Figure 3: Finished 1.75 mm rPET filament spools produced from recycled PET bottles, ready for 3D printing."
          />
          <Figure
            src={`${IMG_BASE}/using_rPET_filament_3D_print.jpg`}
            alt="3D Printing with rPET"
            caption="Figure 4: rPET filament being used in a Bambu Lab X1 Carbon 3D printer to fabricate ASTM D638 test specimens."
          />
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Mechanical Benchmarking & Optimization</h2>
        <p className={styles.paragraph}>
          To determine the material's viability for structural engineering applications, the custom rPET filament was subjected to rigorous mechanical testing. Test specimens were designed to ASTM D638 Type IV specifications and printed with 100% rectilinear infill at 30 mm/s. Across nine different parameter combinations, 45 test specimens were evaluated on a universal testing machine.
        </p>

        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>45.31 MPa</div>
            <div className={styles.metricLabel}>Ultimate Tensile Strength</div>
            <p className={styles.metricSubtext}>
              Comparable to virgin commercial PLA (46.44 MPa).
            </p>
          </div>

          <div className={styles.metricCard}>
            <div className={styles.metricValue}>8.7%</div>
            <div className={styles.metricLabel}>Elongation at Break</div>
            <p className={styles.metricSubtext}>
              Demonstrated superior ductility and impact resistance.
            </p>
          </div>

          <div className={styles.metricCard}>
            <div className={styles.metricValue}>0.901</div>
            <div className={styles.metricLabel}>XGBoost RMSE</div>
            <p className={styles.metricSubtext}>
              Exceptional predictive accuracy for stress-strain curves.
            </p>
          </div>
        </div>

        <p className={styles.paragraph}>
          The experimental results demonstrated that the optimized rPET filament not only matches the tensile strength of standard PLA but significantly outperforms both PLA and ABS in terms of ductility and toughness (2.23 MJ/m³), making it an excellent closed-loop alternative for load-bearing printed parts.
        </p>

        <div className={styles.galleryGrid}>
          <Figure
            src={`${IMG_BASE}/Specimen dimension.jpg`}
            alt="ASTM D638 Specimen Dimensions"
            caption="Figure 5: SolidWorks CAD drawing of the ASTM D638 Type IV tensile test specimen geometry (all dimensions in mm)."
          />
          <Figure
            src={`${IMG_BASE}/PLA ABS PETG rPET Specimens.jpg`}
            alt="Comparative Specimens"
            caption="Figure 6: Comparative 3D-printed specimens (PLA, ABS, PETG, and rPET) before mechanical testing."
          />
          <Figure
            src={`${IMG_BASE}/printed specimens before tensile test.jpg`}
            alt="Before Tensile Test"
            caption="Figure 7: Test specimens labeled by material type prior to UTM loading."
          />
          <Figure
            src={`${IMG_BASE}/printed specimens after tensile test.jpg`}
            alt="After Tensile Test"
            caption="Figure 8: Post-fracture specimens showing brittle failure in PLA/ABS versus ductile necking in rPET."
          />
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Data-Driven Prediction Model</h2>
        <p className={styles.paragraph}>
          Beyond physical mechanical testing, this research integrated advanced computational modeling to accelerate future material engineering. I employed an <strong>XGBoost</strong> machine learning algorithm to predict the continuous, non-linear stress-strain curves of the printed polymers based on variations in printing parameters (e.g., infill density, extrusion multiplier).
        </p>
        <p className={styles.paragraph}>
          Following rigorous hyperparameter tuning via Bayesian Optimization (Optuna), Grid Search, and Randomized Search — evaluated through 15-Fold Group Cross-Validation — the model achieved an exceptional average Root Mean Square Error (RMSE) of <strong>0.901 MPa</strong> and an R² of 0.993. Furthermore, SHAP (SHapley Additive exPlanations) interpretability analysis confirmed that the algorithm based its decisions on genuine physical thermodynamics (Force, Yield Stress, Distortion) rather than mathematical noise.
        </p>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Award & Team Recognition</h2>
        <p className={styles.paragraph}>
          An earlier version of this research received <strong>Second Place (Tie) in the Team Category</strong> at the 2025 AIAA Region VII Student Conference. The work was officially recognized in the AIAA press release announcing the conference winners.
        </p>
        <div className={styles.galleryGrid}>
          <Figure
            src={`${IMG_BASE}/rPET winner certificate AIAA Student Conference VII 2025, 2nd Place.png`}
            alt="AIAA 2nd Place Certificate"
            caption="Figure 9: AIAA 2025 Region VII Student Conference — Second Place Tie certificate in the Team Paper Category."
          />
          <Figure
            src={`${IMG_BASE}/team_member.jpeg`}
            alt="Research Team"
            caption="Figure 10: The research team at Aviation and Aerospace University, Bangladesh (AAUB)."
          />
        </div>
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <a
            href="https://aiaa.org/2025/12/09/aiaa-announces-2025-region-vii-student-conference-winners/"
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: '0.9rem',
              fontWeight: 700,
              color: '#d97706',
              textDecoration: 'none',
              background: 'rgba(217, 119, 6, 0.08)',
              padding: '0.55rem 1.25rem',
              borderRadius: '8px',
              border: '1px solid rgba(217, 119, 6, 0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            🏆 Read AIAA Press Release →
          </a>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Research Publication</h2>
        <p className={styles.paragraph}>
          The full manuscript — <em>"Sustainable Additive Manufacturing using rPET: Extrusion Design, Mechanical Testing, and Data-Driven Prediction"</em> — is currently under review in <strong>Rapid Prototyping Journal</strong>. Visit the dedicated research publication page for the complete methodology, parametric investigation of source material thickness, and the XGBoost-SHAP predictive framework.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
          <button
            onClick={() => navigate('/research/rpet-additive-manufacturing')}
            className={styles.btnPrimary}
          >
            📄 Visit Research Publication →
          </button>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Conclusion</h2>
        <p className={styles.paragraph}>
          This study successfully demonstrated a complete, accessible, and highly efficient system for converting post-consumer PET bottles into functional 3D printing filament. The custom-extruded rPET achieved an ultimate tensile strength of 45.31 MPa — nearly identical to virgin PLA — while offering significantly superior ductility (8.7% elongation) and toughness (2.23 MJ/m³). The integrated XGBoost model (RMSE 0.901 MPa) and SHAP interpretability analysis establish a reliable virtual testing framework for sustainable additive manufacturing. With a 53.8% material recovery rate and 50–75% cost reduction over commercial filament, this work provides a scalable blueprint for decentralized circular manufacturing in aerospace and beyond.
        </p>
      </section>

    </div>
  );
}